import { useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import {
  Cancel,
  Check,
  CheckCircle,
  CircleOutlined,
  Close,
} from '@mui/icons-material';
import type { TooltipProps } from '@mui/material';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetTaskDisplayName } from '../../../../react-query/useGetTasks';
import type { Task, TaskStatus } from '../../../../react-query/useGetTasks';
import {
  black,
  blueUltramarine030,
  green050,
  green400,
  greyLight400,
  greyLight500,
  greyLight700,
  red020,
  white,
  yellowOrange300,
} from '../../../../theme/videa-style-tokens';
import { isDirectorUser as isDirectorUserUtil } from '../../../login/login-utils';

const TASK_ICON_SIZE = 22;

const StyledTaskButton = styled(Button)<{ isBlackAndWhite: boolean }>`
  padding: 0.25em;
  border-radius: 1em;
  display: inline-flex;
  align-items: center;

  .progress svg {
    color: ${greyLight700};
  }

  .progress,
  .status {
    margin-right: 0.375em;
  }

  .status {
    display: flex;

    .status-suggested-icon {
      color: ${({ isBlackAndWhite }) =>
        isBlackAndWhite ? black : yellowOrange300};
    }

    .check-circle-icon {
      color: ${({ isBlackAndWhite }) => (isBlackAndWhite ? black : '#64c668')};
    }

    .status-rejected-icon {
      color: ${({ isBlackAndWhite }) => (isBlackAndWhite ? black : '#d9d9d9')};
    }

    svg {
      height: ${TASK_ICON_SIZE}px;
      width: ${TASK_ICON_SIZE}px;
    }
  }

  .task-label {
    font-size: 0.75em;
    text-align: left;
    text-transform: none;
    color: ${black};

    &.rejected {
      ${({ isBlackAndWhite }) => (isBlackAndWhite ? black : '#b9b9b9')};
      text-decoration: line-through;
    }

    @media (min-width: 1640px) {
      font-size: 1em;
    }
  }

  &.active {
    background-color: ${greyLight400};
  }
`;

const StyledActionButtons = styled.div`
  .button-accept,
  .button-reject {
    text-transform: none;
    background-color: ${white};
    filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25));
    &:hover {
      background-color: ${blueUltramarine030};
    }
    &:disabled,
    &:disabled.selected {
      background-color: ${greyLight500};
      color: #b9b9b9;
    }
  }

  .button-accept {
    color: ${green400};
    margin-right: 0.4em;
    &.selected {
      background-color: ${green050};
    }
  }

  .button-reject {
    color: #ea5656;
    &.selected {
      background-color: ${red020};
    }
  }
`;

interface GamifiedTaskProps {
  disabled?: boolean;
  isBlackAndWhite?: boolean;
  isLoading?: boolean;
  onClick?: (task: Task, status: TaskStatus) => void;
  task: Task;
}

export const GamifiedTask = ({
  disabled = false,
  isBlackAndWhite = false,
  isLoading,
  onClick,
  task,
}: GamifiedTaskProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const user = useSelector((state: any) => state.login.user);
  const isDirectorUser = isDirectorUserUtil(user);
  const getTaskDisplayName = useGetTaskDisplayName();
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const handleClickAccept = () => {
    onClick?.(
      task,
      task.status === 'ACKNOWLEDGED' ? 'SUGGESTED' : 'ACKNOWLEDGED',
    );
    handleClose();
  };

  const handleClickReject = () => {
    onClick?.(task, task.status === 'REJECTED' ? 'SUGGESTED' : 'REJECTED');
    handleClose();
  };

  const tooltipSlotProps: TooltipProps['slotProps'] = {
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -14],
          },
        },
      ],
    },
    tooltip: {
      sx: {
        backgroundColor:
          task.status === 'COMPLETED' ? undefined : 'transparent',
      },
    },
  };

  if (task.status === 'NOT_SUGGESTED') return null;

  return (
    <Tooltip
      // Clicking one button and then clicking outside of that button but within
      // the tooltip causes the tooltip to close. The `disableFocusListener`
      // property prevents that weird behavior.
      disableFocusListener
      disableHoverListener={isLoading || disabled}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      slotProps={tooltipSlotProps}
      title={
        task.status === 'COMPLETED' ? (
          'This task was marked as completed based on PMS data provided by your practice.'
        ) : (
          <StyledActionButtons>
            <Button
              className={classNames('button-accept', {
                selected: task.status === 'ACKNOWLEDGED',
              })}
              disabled={isDirectorUser}
              onClick={handleClickAccept}
              startIcon={<Check />}
            >
              <VideaTypography variant="labelSmallStrong">
                <Trans>Reviewed</Trans>
              </VideaTypography>
            </Button>
            <Button
              className={classNames('button-reject', {
                selected: task.status === 'REJECTED',
              })}
              disabled={isDirectorUser}
              onClick={handleClickReject}
              startIcon={<Close />}
            >
              <VideaTypography variant="labelSmallStrong">
                <Trans>Remove</Trans>
              </VideaTypography>
            </Button>
          </StyledActionButtons>
        )
      }
    >
      <div
      // We need this wrapper div so that the tooltip still activates when the
      // contained task button is disabled (e.g., in the case of the COMPLETED
      // task).
      >
        <StyledTaskButton
          className={classNames({
            active: isOpen && task.status !== 'COMPLETED' && !disabled,
          })}
          disabled={task.status === 'COMPLETED' || disabled}
          data-testid={task.type}
          isBlackAndWhite={isBlackAndWhite}
        >
          {isLoading ? (
            <CircularProgress className="progress" size={TASK_ICON_SIZE} />
          ) : (
            <span className="status">
              {task.status === 'SUGGESTED' && (
                <CircleOutlined className="status-suggested-icon" />
              )}
              {(task.status === 'ACKNOWLEDGED' ||
                task.status === 'COMPLETED') && (
                <CheckCircle className="check-circle-icon" />
              )}
              {task.status === 'REJECTED' && (
                <Cancel className="status-rejected-icon" />
              )}
            </span>
          )}
          <VideaTypography
            className={classNames('task-label', {
              rejected: task.status === 'REJECTED',
            })}
            variant="labelSmallStrong"
          >
            {getTaskDisplayName(task.type)}
          </VideaTypography>
        </StyledTaskButton>
      </div>
    </Tooltip>
  );
};
